"use client";

import { useMemo, useState } from "react";
import {
  AlertTriangle,
  CheckCircle,
  Lightbulb,
  RefreshCw,
  Shield,
  Signal,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { MomTestSection } from "./mom-test-section";
import { cn } from "@/lib/utils";
import { mockLlamaEvaluation } from "@/src/features/demo-contracts/score-derivation";
import type {
  DemoPreview,
  DemoValidationMessage,
  MockEvaluationResult,
} from "@/src/features/demo-contracts/types";
import type { MomTestAnswerMap } from "@/src/features/demo-contracts/score-derivation";

/* ─── Constants ─────────────────────────────────────────────────────────── */

const VALIDATION_QUESTIONS = [
  "Is the pain point specific and clearly bounded?",
  "Do multiple independent sources confirm the same issue?",
  "Is there observable investment (time or money) in solving this?",
  "Does the language reveal urgency or proximity to a solution?",
  "Would a founder act on this without additional research?",
] as const;

/* ─── Helpers ────────────────────────────────────────────────────────────── */

function formatConsistencyScore(score: number): string {
  const normalizedScore = score > 1 ? score / 100 : score;
  const clampedScore = Math.min(1, Math.max(0, normalizedScore));
  return `${Math.round(clampedScore * 100)}%`;
}

function countAnsweredMomTestQuestions(
  momTestAnswers?: MomTestAnswerMap,
): number {
  if (!momTestAnswers) return 0;
  return Object.values(momTestAnswers).filter((a) => a.trim().length > 0)
    .length;
}

/* ─── Validation thoroughness ───────────────────────────────────────────── */

interface ValidationThoroughnessDetail {
  label: string;
  detail: string;
  status: "strong" | "adequate" | "weak";
}

export function deriveValidationThoroughness(
  preview: DemoPreview,
): ValidationThoroughnessDetail[] {
  const details: ValidationThoroughnessDetail[] = [];
  const { painPoints, postsCollected, idealCustomers } = preview;

  const platforms = new Set<string>();
  const communities = new Set<string>();
  for (const pp of painPoints) {
    for (const ev of pp.evidence) {
      platforms.add(ev.platform);
      if (ev.community) communities.add(ev.community);
    }
  }

  const coveredPains = painPoints.filter(
    (pp) => pp.evidence.length >= 2,
  ).length;
  const evidenceCoverage =
    coveredPains >= painPoints.length
      ? "strong"
      : coveredPains >= painPoints.length / 2
        ? "adequate"
        : "weak";

  details.push({
    label: "Evidence coverage",
    detail:
      evidenceCoverage === "strong"
        ? `${coveredPains}/${painPoints.length} pain points have 2+ quotes — each key pain is grounded in real customer language`
        : evidenceCoverage === "adequate"
          ? `${coveredPains}/${painPoints.length} pain points well-cited — some pains lack multi-quote support`
          : `Many pain points lack quote evidence — add more citations to build conviction`,
    status: evidenceCoverage,
  });

  const signalVolume =
    postsCollected && postsCollected >= 20
      ? "strong"
      : postsCollected && postsCollected >= 10
        ? "adequate"
        : "weak";

  details.push({
    label: "Signal volume",
    detail:
      signalVolume === "strong"
        ? `${postsCollected?.toLocaleString() ?? "N/A"} posts scanned — large sample gives confidence patterns are representative`
        : signalVolume === "adequate"
          ? `${postsCollected?.toLocaleString() ?? "N/A"} posts scanned — useful sample, broader data would reduce noise`
          : `Signal volume is thin — findings are directional but need more posts for confident conclusions`,
    status: signalVolume,
  });

  const icpStatus =
    idealCustomers && idealCustomers.length >= 2
      ? "strong"
      : idealCustomers && idealCustomers.length === 1
        ? "adequate"
        : "weak";

  details.push({
    label: "ICP definition",
    detail:
      icpStatus === "strong"
        ? `${idealCustomers!.length} detailed customer profiles defined — product idea is well-targeted to specific buyer segments`
        : icpStatus === "adequate"
          ? `${idealCustomers!.length} customer profile defined — add more segments to validate broad appeal`
          : `No detailed ICP profiles — validate with targeted customer interviews before scaling`,
    status: icpStatus,
  });

  return details;
}

function deriveExpertFeedbackPriority(answeredCount: number): {
  label: "high priority" | "medium priority" | "low priority";
  tone: string;
  borderClassName: string;
  badgeClassName: string;
} {
  if (answeredCount >= 4) {
    return {
      label: "high priority",
      tone: "Mom Test responses are mostly complete — this panel carries more weight now.",
      borderClassName: "border-emerald-400/20 bg-emerald-400/5",
      badgeClassName:
        "border-emerald-400/30 bg-emerald-400/10 text-emerald-200",
    };
  }

  if (answeredCount >= 2) {
    return {
      label: "medium priority",
      tone: "Partial Mom Test coverage gives this panel moderate confidence for now.",
      borderClassName: "border-amber-400/20 bg-amber-400/5",
      badgeClassName: "border-amber-400/30 bg-amber-400/10 text-amber-200",
    };
  }

  return {
    label: "low priority",
    tone: "Without enough Mom Test responses, treat this panel as an early directional signal.",
    borderClassName: "border-white/10 bg-white/[0.02]",
    badgeClassName: "border-white/15 bg-white/5 text-primary-foreground/60",
  };
}

/* ─── Sub-components ─────────────────────────────────────────────────────── */

function ValidationThoroughnessBlock({ preview }: { preview: DemoPreview }) {
  const details = deriveValidationThoroughness(preview);
  const strongCount = details.filter((d) => d.status === "strong").length;
  const overallLabel =
    strongCount >= 3
      ? "Well-validated"
      : strongCount >= 1
        ? "Partially validated"
        : "Early-stage validation";

  return (
    <section className="space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-4">
      <div className="flex items-center gap-2">
        <Shield
          className="size-3.5 shrink-0 text-primary-foreground/40"
          aria-hidden="true"
        />
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
          How complete this validation looks
        </p>
        <Badge className="border-white/15 bg-white/5 font-mono text-[10px] text-primary-foreground/50">
          {overallLabel}
        </Badge>
      </div>

      <div className="space-y-2">
        {details.map(({ label, detail, status }) => (
          <div
            key={label}
            className="flex items-start gap-2 rounded-lg border border-white/10 bg-black/20 p-2.5"
          >
            <div className="mt-0.5 shrink-0">
              {status === "strong" ? (
                <CheckCircle
                  className="size-3 text-emerald-400/80"
                  aria-hidden="true"
                />
              ) : status === "adequate" ? (
                <Lightbulb
                  className="size-3 text-amber-400/80"
                  aria-hidden="true"
                />
              ) : (
                <AlertTriangle
                  className="size-3 text-rose-400/80"
                  aria-hidden="true"
                />
              )}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-mono text-[11px] text-primary-foreground/74 sm:text-[12px]">
                {label}
              </p>
              <p className="mt-0.5 font-mono text-[11px] leading-[1.8] text-primary-foreground/58 sm:text-[12px]">
                {detail}
              </p>
            </div>
          </div>
        ))}
      </div>
    </section>
  );
}

/* ─── Score ring ─────────────────────────────────────────────────────────── */

const RING_RADIUS = 42;
const RING_SIZE = RING_RADIUS * 2;
const STROKE_WIDTH = 6;
const CIRCUMFERENCE = 2 * Math.PI * RING_RADIUS;

function scoreToDashOffset(score: number, circumference: number): number {
  return circumference * (1 - score / 10);
}

type RingColor =
  | "text-rose-400/80"
  | "text-amber-400/80"
  | "text-emerald-400/80";

function answeredMomTestCountToRingColor(answeredCount: number): RingColor {
  if (answeredCount >= 4) return "text-emerald-400/80";
  if (answeredCount >= 2) return "text-amber-400/80";
  return "text-rose-400/80";
}

function ScoreRing({
  score,
  answeredMomTestCount,
}: {
  score: number;
  answeredMomTestCount: number;
}) {
  const color = answeredMomTestCountToRingColor(answeredMomTestCount);
  const dashOffset = scoreToDashOffset(score, CIRCUMFERENCE);

  return (
    <div className="flex flex-col items-center gap-2.5 rounded-xl border border-white/10 bg-black/15 px-4 py-3">
      <span className="sr-only">Validation score: {score} out of 10</span>

      <svg
        width={RING_SIZE}
        height={RING_SIZE}
        viewBox={`0 0 ${RING_SIZE} ${RING_SIZE}`}
        aria-label={`Validation score: ${score} out of 10`}
        className="overflow-visible"
      >
        <circle
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={STROKE_WIDTH}
          className="text-white/[0.06]"
        />
        <circle
          data-testid="validation-score-ring-progress"
          cx={RING_SIZE / 2}
          cy={RING_SIZE / 2}
          r={RING_RADIUS}
          fill="none"
          stroke="currentColor"
          strokeWidth={STROKE_WIDTH}
          strokeDasharray={CIRCUMFERENCE}
          strokeDashoffset={dashOffset}
          strokeLinecap="round"
          transform={`rotate(-90 ${RING_SIZE / 2} ${RING_SIZE / 2})`}
          className={cn("transition-all duration-500 ease-out", color)}
        />
      </svg>

      <span className="font-serif text-4xl font-bold tracking-tight text-primary-foreground">
        {score}
        <span className="font-mono text-[11px] font-normal text-primary-foreground/30">
          /10
        </span>
      </span>
    </div>
  );
}

/* ─── Message item ────────────────────────────────────────────────────────── */

const MESSAGE_ICONS = {
  warning: AlertTriangle,
  suggestion: Lightbulb,
  positive: CheckCircle,
} as const;

const MESSAGE_ICON_COLORS = {
  warning: "text-amber-400/80",
  suggestion: "text-primary-foreground/40",
  positive: "text-emerald-400/80",
} as const;

function MessageItem({ message }: { message: DemoValidationMessage }) {
  const Icon = MESSAGE_ICONS[message.type];
  const iconColor = MESSAGE_ICON_COLORS[message.type];

  return (
    <li className="flex items-start gap-2 font-mono text-[11px] leading-[1.6] text-primary-foreground/65">
      <Icon
        className={cn("mt-0.5 size-3.5 shrink-0", iconColor)}
        aria-hidden="true"
      />
      <span>{message.text}</span>
    </li>
  );
}

/* ─── Validation section ──────────────────────────────────────────────────── */

export function ValidationSection({
  preview,
  momTestAnswers = {},
  onMomTestAnswer,
}: {
  preview: DemoPreview;
  momTestAnswers?: MomTestAnswerMap;
  onMomTestAnswer?: (questionId: string, answer: string) => void;
}) {
  const [refreshVersion, setRefreshVersion] = useState(0);
  const [isEvaluating, setIsEvaluating] = useState(false);

  const evaluationResult: MockEvaluationResult = useMemo(() => {
    void refreshVersion;
    return mockLlamaEvaluation(preview, { momTestAnswers });
  }, [preview, momTestAnswers, refreshVersion]);

  const handleRefresh = () => {
    setIsEvaluating(true);
    setTimeout(() => {
      setRefreshVersion((current) => current + 1);
      setIsEvaluating(false);
    }, 500);
  };

  const score = evaluationResult?.score ?? 0;
  const answeredMomTestCount = countAnsweredMomTestQuestions(momTestAnswers);
  const expertFeedbackPriority =
    deriveExpertFeedbackPriority(answeredMomTestCount);

  return (
    <>
      {/* Score + expert feedback block */}
      <section
        className={cn(
          "space-y-3 rounded-xl border p-4",
          expertFeedbackPriority.borderClassName,
        )}
      >
        <div className="flex flex-col gap-4 sm:flex-row sm:items-start">
          {/* Score ring */}
          <div className="flex shrink-0 justify-center">
            {isEvaluating ? (
              <div className="flex size-[72px] items-center justify-center">
                <span className="font-mono text-[11px] text-primary-foreground/40">
                  Evaluating...
                </span>
              </div>
            ) : (
              <ScoreRing
                score={score}
                answeredMomTestCount={answeredMomTestCount}
              />
            )}
          </div>

          {/* Messages + priority */}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
                Expert feedback
              </p>
              <Badge
                className={cn(
                  "font-mono text-[10px] uppercase tracking-widest",
                  expertFeedbackPriority.badgeClassName,
                )}
              >
                {expertFeedbackPriority.label}
              </Badge>
            </div>
            <p className="mt-1 font-mono text-[11px] leading-[1.8] text-primary-foreground/60 sm:text-[12px]">
              {expertFeedbackPriority.tone}
            </p>
            {evaluationResult && evaluationResult.messages.length > 0 ? (
              <ul className="mt-1.5 space-y-1.5">
                {evaluationResult.messages.map((msg, i) => (
                  <MessageItem key={i} message={msg} />
                ))}
              </ul>
            ) : (
              <p className="mt-1.5 font-mono text-[12px] text-primary-foreground/50">
                No feedback yet.
              </p>
            )}
          </div>
        </div>

        {/* Refresh button */}
        <div className="flex justify-end">
          <button
            type="button"
            onClick={handleRefresh}
            disabled={isEvaluating}
            className={cn(
              "flex items-center gap-1.5 rounded-md border border-white/10 bg-white/5 px-3 py-1.5 font-mono text-[11px] text-primary-foreground/55 transition-colors",
              "hover:border-white/20 hover:bg-white/10",
              "disabled:cursor-not-allowed disabled:opacity-40",
            )}
          >
            <RefreshCw
              className={cn("size-3", isEvaluating && "animate-spin")}
              aria-hidden="true"
            />
            {isEvaluating ? "Evaluating..." : "Refresh"}
          </button>
        </div>
      </section>

      {/* Validation thoroughness */}
      <ValidationThoroughnessBlock preview={preview} />

      {/* Validation metadata */}
      <section className="space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
          What this score is based on
        </p>
        <p className="font-mono text-[12px] leading-[1.7] text-primary-foreground/60 sm:text-[13px]">
          This metadata explains how much raw signal the report saw and how
          consistent the evidence looks.
        </p>
        <div className="space-y-1.5 font-mono text-[12px] leading-[1.8] text-primary-foreground/66 sm:text-[13px]">
          <p>
            Posts collected: {preview.postsCollected?.toLocaleString() ?? "N/A"}
          </p>
          {preview.consistencyScore != null && (
            <p>
              Consistency: {formatConsistencyScore(preview.consistencyScore)}
            </p>
          )}
          {preview.methodology && (
            <p className="leading-relaxed">
              Methodology: {preview.methodology}
            </p>
          )}
        </div>
      </section>

      {/* Mom Test */}
      {onMomTestAnswer && (
        <MomTestSection answers={momTestAnswers} onAnswer={onMomTestAnswer} />
      )}

      {/* Validation questions */}
      <section className="space-y-2 rounded-xl border border-white/10 bg-white/[0.03] p-4">
        <p className="font-mono text-[10px] uppercase tracking-widest text-primary-foreground/40">
          Questions to pressure-test before building
        </p>
        <ul className="space-y-1.5">
          {VALIDATION_QUESTIONS.map((question) => (
            <li
              key={question}
              className="flex items-start gap-2 font-mono text-[12px] leading-[1.8] text-primary-foreground/72 sm:text-[13px]"
            >
              <Signal
                className="mt-0.5 size-3.5 shrink-0 text-primary-foreground/25"
                aria-hidden="true"
              />
              <span>{question}</span>
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
